import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getLiveSuggestion,
  getMarketAnalysis,
  liveSuggestionFor,
  normalizeLiveTimeframe,
} from "../../../redux/live-suggestion/liveSuggestionSlice";
import useServiceStatus from "../../useServiceStatus";

const useLiveSuggestionRefresh = (enabled = true, targets = []) => {
  const dispatch = useDispatch();
  const { isActive: insightOn } = useServiceStatus("insight");
  const { isActive: assistantOn } = useServiceStatus("aiAssistant");
  const signalsOn = insightOn || assistantOn;
  const {
    main: { userData },
    liveSuggestion: { suggestions, liveTimeframe, refreshTick },
  } = useSelector((state) => state);
  const queuedRef = useRef(new Set());
  const tfRef = useRef(null);
  const tickRef = useRef(null);
  const timersRef = useRef([]);
  const [pageVisible, setPageVisible] = useState(
    () => typeof document === "undefined" || document.visibilityState !== "hidden",
  );

  useEffect(() => {
    const onChange = () => setPageVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);

  useEffect(() => () => {
    timersRef.current.forEach((entry) => clearTimeout(entry.timer));
  }, []);

  useEffect(() => {
    const dropWaiting = () => {
      timersRef.current.forEach((entry) => {
        clearTimeout(entry.timer);
        if (!entry.fired) queuedRef.current.delete(entry.key);
      });
      timersRef.current = [];
    };
    if (!enabled || !pageVisible || !userData?.email || !signalsOn) {
      dropWaiting();
      return;
    }
    const boardFrame = normalizeLiveTimeframe(liveTimeframe);
    const timeframeChanged = tfRef.current !== null && tfRef.current !== boardFrame;
    const refreshRequested = tickRef.current !== null && tickRef.current !== refreshTick;
    tfRef.current = boardFrame;
    tickRef.current = refreshTick;
    if (!timeframeChanged && !refreshRequested) {
      dropWaiting();
      return;
    }
    dropWaiting();
    queuedRef.current = new Set();
    const stockTargets = (targets || []).filter(
      (target) => target?.stock && !String(target.symbol || "").includes("="),
    );
    const otherTargets = (targets || []).filter(
      (target) => !(target?.stock && !String(target.symbol || "").includes("=")),
    );
    const missingStocks = stockTargets.filter((target) => {
      const saved = liveSuggestionFor(suggestions, target.symbol, "1d");
      return !(saved && !(saved.miss === "no_model" && !saved.trainRequested));
    });
    if (missingStocks.length) {
      const batchKey = `${refreshTick}|${stockTargets.map((target) => String(target.symbol)).sort().join(",")}`;
      if (!queuedRef.current.has(batchKey)) {
        queuedRef.current.add(batchKey);
        Promise.resolve(
          dispatch(
            getMarketAnalysis(
              userData["cognito:username"],
              userData["custom:custom_username"] || userData.email,
              missingStocks.map((target) => target.symbol),
            ),
          ),
        ).finally(() => {
          queuedRef.current.delete(batchKey);
        });
      }
    } else if (!stockTargets.length) {
      const macroKey = `macro|${refreshTick}`;
      if (!queuedRef.current.has(macroKey)) {
        queuedRef.current.add(macroKey);
        dispatch(
          getMarketAnalysis(
            userData["cognito:username"],
            userData["custom:custom_username"] || userData.email,
            [],
          ),
        );
      }
    }
    otherTargets.forEach((target, index) => {
      const symbol = target?.symbol;
      if (!symbol) return;
      const timeframe = target.stock ? "1d" : boardFrame;
      const saved = liveSuggestionFor(suggestions, symbol, timeframe);
      if (saved && !(saved.miss === "no_model" && !saved.trainRequested)) return;
      const key = `${String(symbol).split("/")[0].toUpperCase()}|${timeframe}`;
      if (queuedRef.current.has(key)) return;
      queuedRef.current.add(key);
      const entry = { key, fired: false, timer: null };
      entry.timer = setTimeout(() => {
        entry.fired = true;
        if (document.visibilityState === "hidden") {
          queuedRef.current.delete(key);
          return;
        }
        dispatch(
          getLiveSuggestion(
            userData["cognito:username"],
            userData["custom:custom_username"] || userData.email,
            target.stock ? "yahoo" : target.exchange || "kucoin",
            symbol,
            false,
            null,
            false,
            false,
            0,
            [],
            timeframe,
            Boolean(target.stock),
          ),
        );
      }, Math.floor(index / 5) * 5000);
      timersRef.current.push(entry);
    });
  }, [dispatch, enabled, liveTimeframe, pageVisible, refreshTick, signalsOn, suggestions, targets, userData]);
};

export default useLiveSuggestionRefresh;
